<script setup>
import { onMounted, ref, nextTick, computed, inject, watch } from 'vue'
import { storeToRefs } from 'pinia'

import { useCalculationStore } from '@/stores/calculation'
import { useShipApiStore } from '@/stores/shipApi'
import { useProjectUserStore } from '@/stores/project_user'
import { useOptimisationStore } from '@/stores/optimisation'
import { useAuthorizationStore } from '@/stores/authorization'

import CalculationInputSideBar from '@/components/calculation-sidebar/CalculationInputSideBar.vue'
import OptimisationCardTable from '@/components/optimisation/OptimisationCardTable.vue'
import CalculationMooringSide from '@/components/calculation/CalculationMooringSide.vue'
import MooringLayoutChart from '@/components/chart/MooringLayoutChart.vue'

import { useRoute } from 'vue-router/dist/vue-router'
import { useDrawerStore } from '@/stores/drawer'

const calculationStore = useCalculationStore()
const shipApiStore = useShipApiStore()
const projectUserStore = useProjectUserStore()
const optimisationStore = useOptimisationStore()
const authorizationStore = useAuthorizationStore()

const { items: authorizations } = storeToRefs(authorizationStore)
const { currentItem: currentProjectUser } = storeToRefs(projectUserStore)
const { currentItem: currentCalculation } = storeToRefs(calculationStore)
const appHeaderHeight = inject('appHeaderHeight')

const route = useRoute()
const drawerStore = useDrawerStore()

const optimisationTable = ref(null)
const optimisationTableHeight = ref(0)

onMounted(async () => {
  drawerStore.resetCalculationDrawer()
  await calculationStore.fetchOptions()
  await projectUserStore.fetchItems({ project: route.params.id })
  projectUserStore.setCurrentItem()
  if (authorizations.value.length === 0) {
    await authorizationStore.fetchItems()
  }
  authorizationStore.setCurrentRole([currentProjectUser.value.role])

  await optimisationStore.fetchOptions()
  await calculationStore.fetchItem(route.params.calculationId)
  await optimisationStore.fetchItem(currentCalculation.value.optimisation)
  await shipApiStore.fetchItems()

  await nextTick()
  optimisationTableHeight.value = optimisationTable.value?.$el?.offsetHeight || 0
  height.value = window.innerHeight
  window.addEventListener('resize', updateViewport)
})

const chartHeightStr = computed(() => {
  return height.value - optimisationTableHeight.value - appHeaderHeight.value + 'px'
})

const height = ref(0)
const mooringLayoutChart = ref(null)

watch(
  () => height.value,
  async () => {
    await nextTick()
    optimisationTableHeight.value = optimisationTable?.value.$el.offsetHeight || 0
  },
  { once: true }
)

const updateViewport = () => {
  height.value = window.innerHeight
  mooringLayoutChart.value?.resizeChart()
}
</script>

<template>
  <v-layout>
    <CalculationInputSideBar />
    <v-card width="50vw" style="padding-left: 55px" variant="text">
      <template v-if="height">
        <MooringLayoutChart :height="chartHeightStr" ref="mooringLayoutChart" />
        <OptimisationCardTable ref="optimisationTable" class="pb-1" />
      </template>
      <div v-else class="d-flex justify-center align-center fill-height">
        <v-progress-circular :size="50" indeterminate color="primary"></v-progress-circular>
      </div>
    </v-card>
    <v-divider vertical />
    <CalculationMooringSide width="50vw" />
  </v-layout>
</template>
